import React from "react";

import type {
  BoxProps,
  TypographyProps,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  Divider,
  Grid,
  Typography,
  useTheme,
} from "@braintrust/braintrust-ui-components";

type SectionProps = {
  title: string;
  style?: React.CSSProperties;
};

export const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({
  title,
  children,
  style,
}) => {
  const theme = useTheme();

  return (
    <Grid
      container
      spacing={4}
      style={{ marginBottom: theme.spacing(8), ...style }}
    >
      <Grid
        size={{
          xs: 12,
          md: 3.5,
        }}
      >
        <SectionTitle>{title}</SectionTitle>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 8,
        }}
      >
        {children}
      </Grid>
    </Grid>
  );
};

type SectionTitleProps = Omit<TypographyProps, "component"> & {
  children: React.ReactNode;
};

const SectionTitle = ({ children, ...props }: SectionTitleProps) => {
  return (
    <Typography
      variant="title"
      size="small"
      fontWeight={400}
      component="h3"
      {...props}
    >
      {children}
    </Typography>
  );
};

type SectionSubtitleProps = Partial<TypographyProps> & {
  children?: React.ReactNode;
  action?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  containerProps?: BoxProps;
  descriptionContainerProps?: BoxProps;
};

export const SectionSubtitle = ({
  children,
  title,
  action,
  description,
  containerProps = {},
  descriptionContainerProps = {},
  ...props
}: SectionSubtitleProps) => {
  return (
    <Box
      {...containerProps}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        gap: 2,
        ...containerProps.sx,
      }}
    >
      <Box sx={{ width: { xs: 1, sm: "auto" } }}>
        <Typography variant="label" size="large" component="h4" {...props}>
          {title || children}
        </Typography>
        {description && (
          <Box
            {...descriptionContainerProps}
            sx={{
              mt: 1,
              mb: { xs: 1, sm: 8 },
              ...descriptionContainerProps.sx,
            }}
          >
            <Typography component="p" color="secondary">
              {description}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: { xs: 1, sm: "auto" },
          my: { xs: 2, sm: 0 },
          flexShrink: 0,
        }}
      >
        {action}
      </Box>
    </Box>
  );
};

type SectionRowProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  divider?: boolean;
};

export const SectionRow: React.FC<React.PropsWithChildren<SectionRowProps>> = ({
  title,
  subtitle,
  divider,
  children,
  ...props
}) => {
  return (
    <Box {...props} sx={{ flexWrap: "wrap", mb: 3 }}>
      {title && (
        <Typography component="p" variant="label" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {subtitle && <Typography component="p">{subtitle}</Typography>}
      {children}
      {divider && (
        <Box sx={{ mt: 3 }}>
          <Divider color="secondary" />
        </Box>
      )}
    </Box>
  );
};
